/**
 * Developer Help recipe content — the four "Cortex Recipe Cards" covering
 * create agent → attach capabilities → connect from code.
 *
 * Kept aligned with the Markdown series in Requirements/guides_dev-*.md
 * (the repo docs are the source of truth for API accuracy).
 */

import type { Recipe } from './types';
import {
  addFunctionCurl,
  assignSkillCurl,
  chatCompletionSample,
  createAgentCurl,
  multiTurnSample,
  streamingSample,
} from './codeSamples';

export const RECIPE_SLUGS = {
  map: 'map',
  createAgent: 'create-agent',
  capabilities: 'capabilities',
  connect: 'connect',
} as const;

export const getRecipes = (baseUrl: string): Recipe[] => [
  {
    slug: RECIPE_SLUGS.map,
    number: 0,
    title: 'The Builder Map',
    time: '~5 min read',
    goal: 'Understand the four words that matter — agent, slug, session, capability — and the journey ahead.',
    ingredients: ['Nothing to install. This one is just reading.'],
    steps: [
      {
        title: 'An agent is a saved template, not a running process',
        body:
          'An agent is an AgentDefinition: a name, a permanent slug, a system prompt, a default model, and a list of attached capabilities. You create it once; every conversation runs from that template. Think of it as a recipe for a chef, not the chef at work.',
      },
      {
        title: 'The slug is the permanent API address',
        body:
          'Every API call embeds the slug: POST /v1/agents/{slug}/chat/completions. It cannot be changed after creation, so pick it carefully — your integration code will depend on it.',
      },
      {
        title: 'Sessions give you free multi-turn memory',
        body:
          'A session is one running conversation. Chat responses return a session_id; pass it back on the next request and the server remembers the history. Your app never re-sends old messages.',
      },
      {
        title: 'Capabilities are anything the agent can do beyond chat',
        body:
          'Tools, custom functions, skills, MCP servers, child agents, and knowledge files — all attached from the Agent Detail page or one REST call each. Recipe 2 shows when to use which.',
      },
      {
        title: 'Everything is scoped to a tenant and a project',
        body:
          'Agents, API keys, tools, and sessions all live inside a project. Your API key belongs to one project, so it only sees and calls that project’s agents. A common pattern: build and tune in the portal, then call from code.',
      },
    ],
    tasteTest: [
      'You can explain the difference between an agent (template) and a session (conversation) to a teammate.',
      'You know which slug your integration will call.',
    ],
    burns: [
      'Skipping the map and inventing endpoints — the only invoke route you need to remember is /v1/agents/{slug}/chat/completions.',
      'Confusing project scope: if something is "missing," check the tenant/project selector first.',
    ],
    nextSlug: RECIPE_SLUGS.createAgent,
  },
  {
    slug: RECIPE_SLUGS.createAgent,
    number: 1,
    title: 'Create an Agent',
    time: '~10 min',
    goal: 'A named agent with a slug, system prompt, and model — visible at /agents and addressable by API.',
    ingredients: [
      'A tenant and project selected in the top bar.',
      'An LLM provider configured for the tenant/project (Settings → LLM Configs).',
      'For the API path: a JWT (portal login) or an API key with the agents:write scope.',
    ],
    steps: [
      {
        title: 'Portal: Agents → New agent',
        body:
          'Open AI Operations → Agents and click Create Agent. Fill in Name, Slug (auto-generated but immutable — set it deliberately), and a specific System Prompt: name the role, audience, allowed data, and what to do when unsure. Pick a Default Model. Leave advanced flags at their defaults.',
      },
      {
        title: 'Or one REST call',
        body:
          'POST /api/agents creates the same thing. The slug is optional (generated from name), but provide it explicitly so your integration never guesses. defaultModelId can be set now or later in the portal.',
        code: createAgentCurl(baseUrl),
      },
      {
        title: 'Know the rest of the CRUD surface',
        body:
          'GET /api/agents lists, GET /api/agents/slug/{slug} fetches by slug, PUT /api/agents/{id} updates, POST /api/agents/{id}/clone copies (the escape hatch for a bad slug), DELETE /api/agents/{id} removes.',
      },
    ],
    tasteTest: [
      'Open the agent and send "Introduce yourself" in the chat panel — the reply should reflect your system prompt.',
      'GET /api/agents/slug/{your-slug} returns the definition with its id.',
    ],
    burns: [
      'Slug regret: slugs are immutable. Clone the agent with a new slug and retire the old one.',
      'No model configured: chat fails until an LLM provider is set up under Settings → LLM Configs.',
      '"You are a helpful assistant" produces generic mush — write a specific system prompt.',
      'System agents are read-only; clone them into your project to customize.',
    ],
    nextSlug: RECIPE_SLUGS.capabilities,
  },
  {
    slug: RECIPE_SLUGS.capabilities,
    number: 2,
    title: 'Give It Powers',
    time: '~15 min',
    goal: 'An agent that can call tools, follow skills, and use external systems — not just chat.',
    ingredients: [
      'An agent from Recipe 1 and its Agent Detail page (/agents/{id}).',
      'JWT or an API key with agents:write for the REST variants.',
    ],
    steps: [
      {
        title: 'Tools — the fastest power-up',
        body:
          'Tools are pre-registered actions from the tool catalog (AI Operations → Tools). Set includeAllDefaultTools for prototyping, or hand-pick tools on the Agent Detail page (POST /api/agents/{agentId}/functions/attach-tool). Production tip: give an agent only the tools its job needs — smaller toolsets mean better tool choice.',
      },
      {
        title: 'Functions — your code as a capability',
        body:
          'Functions are OpenAI-style definitions the model can call; your app executes them. Write the description for the model, not for humans — state clearly when it should (and should not) be called.',
        code: addFunctionCurl(baseUrl),
      },
      {
        title: 'Skills — reusable playbooks',
        body:
          'A skill is an instruction bundle (SKILL.md plus supporting files) injected into the agent’s context. Upload once via AI Operations → Skills (POST /api/skills), then assign it to the agent. Pass versionNumber to pin a version; omit it to track the latest.',
        code: assignSkillCurl(baseUrl),
      },
      {
        title: 'MCP servers — external tool ecosystems',
        body:
          'Register a Model Context Protocol server under Integrations → MCP Servers (POST /api/mcp-servers), then assign it with POST /api/mcp-servers/agents/{agentId}. Its tools become available to the agent. Check the server’s health indicator before blaming the agent for missing tools.',
      },
      {
        title: 'Child agents and knowledge — delegation and grounding',
        body:
          'Attach specialist agents to a parent (POST /api/agents/{parentId}/attached-agents) so it can delegate sub-tasks. Upload documents on the Knowledge tab (/api/agents/{agentId}/knowledge-files) so answers come from your content. Advanced: connector Actions (Integrations → Connectors) package governed external app access — revisit them once the basics work.',
      },
    ],
    tasteTest: [
      'Ask something that requires the new power — the session detail (/sessions/{id}) shows every tool call and result.',
      'For skills, confirm the response follows the skill’s format and the skill appears in the session’s skills_context.',
    ],
    burns: [
      'The model ignores your function: almost always a weak description. Say when to call it, with example inputs.',
      'Everything enabled, nothing works well: all default tools plus several MCP servers gives the model too many choices. Trim to the job.',
      'MCP tools missing: the server may be unhealthy or its auth config wrong.',
      'parametersJson must be valid JSON Schema; invalid schemas fail silently at tool-choice time.',
    ],
    nextSlug: RECIPE_SLUGS.connect,
  },
  {
    slug: RECIPE_SLUGS.connect,
    number: 3,
    title: 'Connect & Call',
    time: '~10 min',
    goal: 'Your application sends a message and gets your agent’s answer back — in any language.',
    ingredients: [
      'An agent slug from Recipe 1.',
      'A project API key (Workspace → API Keys) with agents:read and agents:execute scopes. The full key is shown once — store it in a secret manager.',
    ],
    steps: [
      {
        title: 'The one endpoint to remember',
        body:
          'POST {baseUrl}/v1/agents/{slug}/chat/completions with an Api-Key header. It is wire-compatible with OpenAI Chat Completions: any OpenAI SDK works if you point its base_url at {baseUrl}/v1/agents/{slug} and use model name "cortex".',
        code: chatCompletionSample(baseUrl),
      },
      {
        title: 'Multi-turn: pass session_id back',
        body:
          'Every response includes a session_id. Send it on the next request and the server keeps the conversation history — your app only sends the new message. Persist it alongside your user’s conversation.',
        code: multiTurnSample(baseUrl),
      },
      {
        title: 'Streaming',
        body: 'Set stream: true and consume server-sent events, exactly like OpenAI streaming.',
        code: streamingSample(baseUrl),
      },
      {
        title: 'Going further',
        body:
          'Same slug and auth, different styles: the Responses API (/v1/agents/{slug}/responses) for structured output and function calling; the Assistants API (beta v2) for long-running threads; Transcriptions for audio. The API Explorer (AI Operations → API Explorer) generates and runs these calls per agent.',
      },
    ],
    tasteTest: [
      'The cURL call returns choices[0].message.content in your agent’s voice.',
      'A follow-up with the returned session_id references the earlier turn.',
    ],
    burns: [
      '401 Unauthorized: programmatic calls use the Api-Key header, and the key must be active and unexpired.',
      '403 or agent not found: API keys are project-scoped — the agent must live in the same project as the key.',
      'Read-only keys can list agents but not run them; recreate the key with agents:execute.',
      'Forgetting session_id starts a fresh conversation on every request.',
      'With OpenAI SDKs the model is always "cortex" — the actual LLM is decided by the agent’s configuration.',
    ],
  },
];
